/**
 * Stacking + hit-testing contract for the call surfaces.
 *
 * Documented order (see app-lock-screen.tsx / the biometric soft-lock work):
 *   app-lock + web-MFA sheets  z-50   — full-screen modal ABOVE app chrome
 *                                       (pointer-events auto + inert on routes)
 *                                       and BELOW call overlays so a ringing/
 *                                       live call stays answerable
 *   call overlays (voice/video/group)  z-60, in-overlay chrome z-61…z-65
 *   return-to-call banner      z-66   — must be ABOVE the overlays: the overlay
 *                                       stays MOUNTED while minimized (audio +
 *                                       video tiles stay bound), so a banner
 *                                       underneath it is unreachable
 *   dialogs / sheets / lightbox z-70+  — unaffected
 *
 * The banner was z-59 (under the still-mounted z-60 overlay) and the overlays
 * hid themselves with a bare `invisible`, which does NOT remove the subtree
 * from hit-testing: on the Android WebView the live, hardware-composited
 * <video> surfaces kept swallowing taps over the banner, so a minimized video
 * call could not be returned to. Fixed by raising the banner above the overlay
 * AND adding `pointer-events-none` to the minimized overlay.
 */

/** Soft-lock / web-MFA sheets. */
export const APP_LOCK_Z_INDEX = 50
/** Full-screen voice/video/group call overlay root. */
export const CALL_OVERLAY_Z_INDEX = 60
/** Highest z-index used by chrome INSIDE a call overlay (reconnect banner, etc). */
export const CALL_OVERLAY_CHROME_MAX_Z_INDEX = 65
/** The "Return to call" pill rendered at app-shell level while minimized. */
export const RETURN_TO_CALL_BANNER_Z_INDEX = 66

/** Tailwind class for {@link APP_LOCK_Z_INDEX}. */
export const APP_LOCK_Z_CLASS = 'z-[50]'
/** Tailwind class for {@link CALL_OVERLAY_Z_INDEX}. */
export const CALL_OVERLAY_Z_CLASS = 'z-[60]'
/** Tailwind class for {@link RETURN_TO_CALL_BANNER_Z_INDEX}. */
export const RETURN_TO_CALL_BANNER_Z_CLASS = 'z-[66]'

/**
 * Soft-lock / web-MFA blocks the app chrome whenever locked — including
 * during a live call (product decision, RC security audit): a soft-locked
 * user who answers a call must not be able to browse Sessions / Calls /
 * Profile / Admin behind it. The call surfaces themselves stay fully usable
 * because every one of them (overlays, return-to-call banner, call-waiting /
 * gate / consent prompts, in-call chat drawer) renders OUTSIDE the gated
 * chrome container at z-60+ — see App.tsx.
 */
export function shouldBlockAppChromeForSoftLock(locked: boolean): boolean {
  return locked
}

/**
 * Classes applied to a call overlay root while the user has minimized it.
 *
 * `invisible` alone keeps the subtree in the hit-test tree (and Android's
 * composited video surfaces on top of it), so `pointer-events-none` is what
 * actually lets the return-to-call banner receive the tap.
 */
export function minimizedCallOverlayClassName(minimized: boolean): string {
  return minimized ? 'pointer-events-none invisible' : ''
}
